import { AxiosResponse } from "axios";
import { apiClient } from "./ApiClient";

// Mock theme data
const MOCK_THEME_DATA = {
    id: "029154C22EFFAC785952202C2BFF8BD7",
    preview:
        "https://s3-eu-west-1.amazonaws.com/spond-web-production/static/Theme-image1.png",
    type: "SCRATCHCARDS",
    primaryColour: "#e51d1f",
    primaryStrokeColour: "#ffffff",
    primaryButtonColour: "#e51d1f",
    primaryButtonTextColour: "#ffffff",
    primaryButtonStrokeColour: "#ffffff",
    themeImage:
        "https://s3-eu-west-1.amazonaws.com/spond-web-production/static/Theme-image1.png",
    background:
        "https://spond.com/images/64e/64e5624c3455352671887f3e6517b763.jpeg",
    prizeInfoImage:
        "https://spond.com/images/dbd/dbd58c526276e83609bd29ed7a58964b.png",
    scratchImage:
        "https://spond.com/images/a0e/a0e63e0a65caa64eb9ed8437cc3a860f.png",
    loserBackground:
        "https://spond.com/images/930/93008fd0053233d573b7fab191882261.png",
    winnerBackground:
        "https://spond.com/images/43a/43af583481c838033f21b02d41fd9f2e.png",
    landingBackground:
        "https://spond.com/images/71b/71b844834c5500e76eb1006674b54cbe.jpeg",
    fullscreenBackground:
        "https://spond.com/images/371/371bdd8472d9715478054d0b64c0a375.jpeg",
    landingScratchImage:
        "https://spond.com/images/881/881662b70fdc6a249b7541833c1ab4b8.png",
    metaImage:
        "https://s3-eu-west-1.amazonaws.com/spond-web-production/static/Theme-image1.png",
    prizeProviderLogo:
        "https://s3-eu-west-1.amazonaws.com/spond-web-production/static/Theme-image1.png",
    previewUrl:
        "https://bidra.spond.com/fundraising/preview/029154C22EFFAC785952202C2BFF8BD7",
    layout: "DEFAULT",
};

// Mock products data for template
const MOCK_PRODUCTS_DATA = {
    products: [
        {
            id: "1",
            name: "Test Product 1",
            description: "A test product for development",
            price: 100,
            image: "https://via.placeholder.com/300x200",
        },
        {
            id: "2",
            name: "Test Product 2",
            description: "Another test product for development",
            price: 250,
            image: "https://via.placeholder.com/300x200",
        },
    ],
    deliveryTime: "2-3 weeks",
};

// Mock template data
const MOCK_TEMPLATE_DATA = {
    id: "1",
    name: "Test Template",
    spotTheBallInfo: {
        priceTiers: [
            { price: 10, tickets: 1 },
            { price: 25, tickets: 3 },
            { price: 50, tickets: 7 },
        ],
    },
    superdrawInfo: {
        prizes: [
            {
                id: "1",
                name: "Grand Prize",
                value: 10000,
                previewUrl:
                    "https://s3-eu-west-1.amazonaws.com/spond-web-production/static/Theme-image1.png",
            },
            {
                id: "2",
                name: "Second Prize",
                value: 1000,
                previewUrl:
                    "https://s3-eu-west-1.amazonaws.com/spond-web-production/static/Theme-image1.png",
            },
            {
                id: "3",
                name: "Third Prize",
                value: 100,
                previewUrl:
                    "https://s3-eu-west-1.amazonaws.com/spond-web-production/static/Theme-image1.png",
            },
        ],
        drawTime: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
        priceTiers: [
            { price: 5, tickets: 1 },
            { price: 10, tickets: 3 },
            { price: 20, tickets: 7 },
        ],
    },
};

class MockApiClient {
    private originalGetTheme: any;
    private originalGetProductsByTemplateId: any;
    private originalGetTemplate: any;
    private isMockingEnabled: boolean = false;

    constructor() {
        // Store original methods
        this.originalGetTheme = apiClient.getTheme.bind(apiClient);
        this.originalGetProductsByTemplateId =
            apiClient.getProductsByTemplateId.bind(apiClient);
        this.originalGetTemplate = apiClient.getTemplate.bind(apiClient);
    }

    enableMocking() {
        if (this.isMockingEnabled) return;

        console.log("🎭 MockApiClient: Enabling API mocking for development");

        // Mock getTheme
        apiClient.getTheme = (
            themeId: string,
            activity: string
        ): Promise<AxiosResponse<any>> => {
            console.log(
                `🎭 MockApiClient: Mocking getTheme(${themeId}, ${activity})`
            );

            // Create a mock response that matches AxiosResponse structure
            const mockResponse: AxiosResponse<any> = {
                data: {
                    ...MOCK_THEME_DATA,
                    id: themeId, // Use the requested theme ID
                    type: this.getTypeFromActivity(activity),
                },
                status: 200,
                statusText: "OK",
                headers: {},
                config: {} as any,
                request: {},
            };

            // Simulate network delay
            return new Promise((resolve) => {
                setTimeout(() => resolve(mockResponse), 100);
            });
        };

        // Mock getProductsByTemplateId
        apiClient.getProductsByTemplateId = (
            templateId: string
        ): Promise<AxiosResponse<any>> => {
            console.log(
                `🎭 MockApiClient: Mocking getProductsByTemplateId(${templateId})`
            );

            const mockResponse: AxiosResponse<any> = {
                data: MOCK_PRODUCTS_DATA,
                status: 200,
                statusText: "OK",
                headers: {},
                config: {} as any,
                request: {},
            };

            return new Promise((resolve) => {
                setTimeout(() => resolve(mockResponse), 100);
            });
        };

        // Mock getTemplate
        apiClient.getTemplate = (
            templateId: string
        ): Promise<AxiosResponse<any>> => {
            console.log(`🎭 MockApiClient: Mocking getTemplate(${templateId})`);

            const mockResponse: AxiosResponse<any> = {
                data: {
                    ...MOCK_TEMPLATE_DATA,
                    id: templateId,
                },
                status: 200,
                statusText: "OK",
                headers: {},
                config: {} as any,
                request: {},
            };

            return new Promise((resolve) => {
                setTimeout(() => resolve(mockResponse), 100);
            });
        };

        this.isMockingEnabled = true;
    }

    disableMocking() {
        if (!this.isMockingEnabled) return;

        console.log("🎭 MockApiClient: Disabling API mocking");

        // Restore original methods
        apiClient.getTheme = this.originalGetTheme;
        apiClient.getProductsByTemplateId =
            this.originalGetProductsByTemplateId;
        apiClient.getTemplate = this.originalGetTemplate;

        this.isMockingEnabled = false;
    }

    private getTypeFromActivity(activity: string): string {
        const activityTypeMap: { [key: string]: string } = {
            scratchcards: "SCRATCHCARDS",
            products: "PRODUCTS",
            "spot-the-ball": "SPOT_THE_BALL",
            "super-draw": "SUPERDRAW",
            raffle: "RAFFLE",
        };

        return activityTypeMap[activity] || "SCRATCHCARDS";
    }

    // Method to update mock data dynamically
    updateMockThemeData(newData: Partial<typeof MOCK_THEME_DATA>) {
        Object.assign(MOCK_THEME_DATA, newData);
        console.log("🎭 MockApiClient: Updated mock theme data", newData);
    }

    // Method to check if mocking is enabled
    isMocking(): boolean {
        return this.isMockingEnabled;
    }
}

// Create singleton instance
export const mockApiClient = new MockApiClient();

// Auto-enable mocking in development mode
if (process.env.NODE_ENV === "development") {
    // Check if we're in preview mode (URL contains /preview/)
    if (window.location.pathname.includes("/preview/")) {
        mockApiClient.enableMocking();
    }
}

// Export for manual control
export default mockApiClient;
